/**
 * The ONE canonical richtext allowlist policy — pure data + pure functions, no DOM, no framework.
 *
 * Rich text is the only place free-form HTML enters the system, and that HTML is served
 * UNAUTHENTICATED to arbitrary visitors on a shared multi-tenant host. This policy is the single source
 * of truth shared by EVERY sanitizer entry point so they can never drift:
 *   - the WRITE-time barrier in the builder (Tiptap onUpdate, browser DOMPurify),
 *   - the RENDER-time barrier in ui-react (`sanitizeRichtextHtml`, isomorphic-dompurify — the storefront
 *     Astro SSR path too), and
 *   - the dashboard's live previews, which must show EXACTLY what the shop will show.
 *
 * It lives in this framework-free package because all four consumers (dashboard/Angular, builder/React,
 * ui-react, storefront) already depend on it and none of them can depend on each other.
 *
 * Keep every Tiptap extension set in LOCKSTEP with this list: no editor may emit a tag/attr/CSS property
 * this policy would strip. Breaking that rule does not fail loudly — it produces a control that appears
 * to work in the editor and silently does nothing on the shop, which is the worst outcome we ship.
 */
export const RICHTEXT_ALLOW = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        's',
        // The editor's toolbar offers H1 and H2 (and `# ` typed makes an h1): with h1 missing here the seller's
        // heading reached the shop as a bare run of text. The page keeps its own title as the first h1; a second
        // one is the seller's call, as it is on Shopify.
        'h1',
        'h2',
        'h3',
        'ul',
        'ol',
        'li',
        'a',
        // Inline images (uploaded via the editor's imageUploader → a real URL). `src` is held to the same
        // URL allowlist as `href` below (http(s)/root-relative only — NEVER data:/javascript:), and only
        // src/alt/title cross; every event handler (onerror/onload/…) is stripped (not in ALLOWED_ATTR).
        'img',
        'blockquote',
        // The carrier for inline text styling (colour / highlight / size). It holds no behaviour of its own:
        // everything it can express is bounded by ALLOWED_STYLE_PROPS below.
        'span',
    ],
    // `style` is allowed ONLY as a channel for the four properties below, and only after
    // `sanitizeStyleAttribute` has rewritten it. It is never passed through as authored.
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'style'],
    // Only http(s), mailto, and root-relative URLs — NEVER javascript: or data: (the classic XSS vectors).
    // Enforced on BOTH `href` and `img[src]` by the sanitizers' uponSanitizeAttribute hook.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|\/)/i,
    /**
     * The CLOSED set of CSS properties a seller may express. Everything else is dropped.
     *
     * Deliberately tiny, and it must stay that way. CSS on a page you do not control is an attack surface
     * of its own: `position`/`z-index` builds an invisible overlay over a Buy button, `content` injects
     * text, and any property taking `url()` is an outbound request that leaks the visitor. None of these
     * four can do any of that — they change how characters look, nothing else.
     */
    ALLOWED_STYLE_PROPS: ['color', 'background-color', 'font-size', 'text-align'],
};
/** A colour: #rgb / #rrggbb / #rrggbbaa, rgb()/rgba() with numbers only, or a bare CSS keyword. */
const COLOUR = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[0-9.,%\s/]+\)|[a-z]{3,20})$/i;
/** A length with a unit we accept. The magnitude is bounded separately, per unit. */
const FONT_SIZE = /^(\d{1,3}(?:\.\d{1,2})?)(px|pt|em|rem|%)$/i;
const ALIGN = /^(?:left|center|right|justify)$/i;
/** Largest value accepted per unit — a 4000px title is not a style, it is a broken page. */
const FONT_SIZE_MAX = { px: 200, pt: 150, em: 12, rem: 12, '%': 500 };
/** A whole style attribute longer than this is not authored text; it is someone probing. */
const MAX_STYLE_LENGTH = 400;
function isAllowedValue(prop, value) {
    switch (prop) {
        case 'color':
        case 'background-color':
            return COLOUR.test(value);
        case 'text-align':
            return ALIGN.test(value);
        case 'font-size': {
            const m = FONT_SIZE.exec(value);
            if (!m)
                return false;
            const magnitude = Number(m[1]);
            const max = FONT_SIZE_MAX[(m[2] ?? '').toLowerCase()];
            return max !== undefined && magnitude > 0 && magnitude <= max;
        }
        default:
            return false;
    }
}
/**
 * Rewrite a `style` attribute down to the allowlisted properties, dropping everything else.
 *
 * Returns the cleaned declarations, or `''` when nothing survives — the caller then drops the attribute
 * entirely rather than leaving `style=""` on the element.
 *
 * This is an ALLOWLIST, not a blocklist: an unknown property is dropped without needing to be known as
 * dangerous first. `expression()`, `url(…)`, `!important`, CSS comments and escapes never reach a value
 * check, because their property name is not one of the four we accept — and inside those four, the value
 * grammars above admit only colours, lengths and four keywords.
 */
export function sanitizeStyleAttribute(value) {
    if (typeof value !== 'string' || value.length === 0 || value.length > MAX_STYLE_LENGTH)
        return '';
    const kept = [];
    const seen = new Set();
    for (const declaration of value.split(';')) {
        const colon = declaration.indexOf(':');
        if (colon <= 0)
            continue;
        const prop = declaration.slice(0, colon).trim().toLowerCase();
        const raw = declaration
            .slice(colon + 1)
            .trim()
            .toLowerCase();
        if (seen.has(prop))
            continue;
        if (!RICHTEXT_ALLOW.ALLOWED_STYLE_PROPS.includes(prop))
            continue;
        if (!isAllowedValue(prop, raw))
            continue;
        seen.add(prop);
        kept.push(`${prop}: ${raw}`);
    }
    return kept.join('; ');
}
//# sourceMappingURL=richtext-policy.js.map